"use client";

import { useEffect, useState } from "react";

export default function useCurrentLocation() {
  const [location, setLocation] = useState(() => {
    // ✅ Load from localStorage first
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem("userLocation");
        if (stored) {
          return JSON.parse(stored);
        }
      } catch {}
    }

    // fallback initial
    return {
      latitude: null,
      longitude: null,
      state: null,
      district: null,
      loading: true,
      error: null,
      permission: "prompt",
    };
  });

  useEffect(() => {
    if (!navigator.geolocation) {
      updateLocation({
        loading: false,
        error: "Geolocation not supported",
      });
      return;
    }

    navigator.permissions?.query({ name: "geolocation" }).then((result) => {
      if (result.state === "denied") {
        updateLocation({
          loading: false,
          error: "Location permission denied",
          permission: result.state,
        });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await res.json();

            const address = data?.address || {};

            const state =
              address.state ||
              address.region ||
              address.state_district ||
              null;

            const district =
              address.county ||
              address.city ||
              address.town ||
              address.village ||
              null;

            updateLocation({
              latitude,
              longitude,
              state,
              district,
              loading: false,
              error: null,
              permission: result.state,
            });
          } catch {
            updateLocation({
              latitude,
              longitude,
              loading: false,
              error: "Failed to fetch location details",
              permission: result.state,
            });
          }
        },
        (error) => {
          updateLocation({
            loading: false,
            error: error.message,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
        }
      );
    });
  }, []);

  // ✅ central update (sync with localStorage)
  const updateLocation = (newData) => {
    setLocation((prev) => {
      const updated = { ...prev, ...newData };

      if (typeof window !== "undefined") {
        localStorage.setItem("userLocation", JSON.stringify(updated));
      }

      return updated;
    });
  };

  return location;
}